import { z } from 'zod';
import { Timestamp, Uuid } from './envelope';

/**
 * Customer-defined attributes.
 *
 * The read model that makes "you can extend this yourself" a checkable claim
 * rather than a sentence in a meeting: a declared attribute has a type the
 * database enforces, a description a client reads in the import template, and a
 * value that is `null` when nobody has filled it in — which the interface
 * renders as "Not recorded", like every other absent value in this product.
 *
 * `dataType` is deliberately the same vocabulary as `DatasetFieldType`. That is
 * what lets a declared attribute pass through the existing validator, the
 * existing exception report and the existing template generator without any of
 * them learning a new concept.
 */

export const CustomAttributeType = z.enum(['text', 'number', 'date', 'boolean', 'enum']);
export type CustomAttributeType = z.infer<typeof CustomAttributeType>;

export const CustomAttributeDefinition = z.object({
  id: Uuid,
  /** Which record type it may be attached to, e.g. `Application`. */
  entityTypeCode: z.string(),
  /** The column name in the import template and the export, without its prefix. */
  code: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  label: z.string(),
  dataType: CustomAttributeType,
  /** The permitted values for an enum; null for every other type. */
  allowedValues: z.array(z.string()).nullable(),
  description: z.string(),
  /** The header a client will see in their template. */
  templateColumn: z.string(),
  createdAt: Timestamp,
  createdBy: z.string().nullable(),
  /** How many records carry a value. The blast radius of retiring it. */
  valueCount: z.number().int().nonnegative(),
});
export type CustomAttributeDefinition = z.infer<typeof CustomAttributeDefinition>;

export const CustomAttributeValue = z.object({
  code: z.string(),
  label: z.string(),
  dataType: CustomAttributeType,
  /** Null means nobody has recorded it — never an empty string, never a zero. */
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
});
export type CustomAttributeValue = z.infer<typeof CustomAttributeValue>;

export const EntityCustomAttributes = z.object({
  entityId: Uuid,
  entityTypeCode: z.string(),
  values: z.array(CustomAttributeValue),
  /** Null when the caller may edit them; otherwise which entitlement is missing. */
  cannotEditReason: z.string().nullable(),
});
export type EntityCustomAttributes = z.infer<typeof EntityCustomAttributes>;

/**
 * What a customer may and may not extend, said in the response rather than in
 * documentation.
 *
 * The relationship model is not user-extensible and that is a position, not a
 * gap: 89 endpoint rules enforced by the database are what make an impact
 * traversal an answer instead of an opinion. A screen that offered to add an
 * edge type and then refused would be worse than one that explains why it does
 * not offer it.
 */
export const ExtensibilityView = z.object({
  attributes: z.array(CustomAttributeDefinition),
  /** Entity types an attribute may be declared for, in this build. */
  extensibleTypes: z.array(z.object({ code: z.string(), label: z.string() })),
  relationshipModelNote: z.string(),
  /** Null when the caller may declare attributes. */
  cannotDeclareReason: z.string().nullable(),
});
export type ExtensibilityView = z.infer<typeof ExtensibilityView>;
